import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSyncExternalStore } from "react";

/* ══════════════════════════════════════════════════════════════════
   Die Touch-Diagnose — was der erste Tipp wirklich trifft (20.09.2026).

   Luca, zum dritten Mal am selben Fehler: *„Nope. Ich muss immer noch
   zweimal drücken. … Wenn ich das über das X schließe, habe ich das Problem
   nicht. Das muss ein generelles Problem sein im Mobile UI."*

   Zweimal gemessen, zweimal eine Ursache gefunden und behoben — und der
   Fehler ist weiter da. Der Grund, warum er sich nicht fangen lässt: Im
   iOS-Simulator unter WebDriver gibt es **keine Softtastatur**, und ohne sie
   fehlt genau der Teil, der den Tipp schluckt. Was fehlt, ist kein weiterer
   Verdacht, sondern ein **Mitschrieb vom echten Gerät**.

   **Deshalb dieses Fenster.** Es hängt an nichts, ändert nichts und
   schreibt nur auf, was der Browser meldet:

   | Zeile | was sie sagt |
   |---|---|
   | `ab 1.234` | Millisekunden seit dem ersten Ereignis |
   | `tipp 195,812 → BUTTON.hleiste-punkt` | wo getippt wurde und **was an dieser Stelle liegt** (`elementFromPoint`) |
   | `klick BUTTON.hleiste-punkt` | ob daraus ein Klick wurde — bleibt er aus, hat ihn jemand geschluckt |
   | `‼ KEIN KLICK` | genau der verlorene Tipp: losgelassen, und 500 ms später kam nichts |
   | `abbruch` | der Browser hat die Berührung an sich genommen (`pointercancel`) — dann kommt nie ein Klick |
   | `fokus …` | wohin der Fokus ging (`focusin`/`focusout`) |
   | `blatt 2 → 1 · ovf hidden` | ein Blatt ging auf oder zu, und was dabei an `body` stand |
   | `tast ueber 336` | Merkmal und Höhe der Tastatur (`schale/tastatur.ts`) |
   | `sicht 390×508` | der sichtbare Ausschnitt — er sagt, ob die Tastatur wirklich steht |
   | `akt BUTTON.hweg-zeile` | worauf der Fokus im Moment des Tipps lag |

   **Die zweite Fassung markiert den Fehler selbst** (20.09.2026). Der erste
   Mitschrieb vom Gerät hörte eine Zeile vor der entscheidenden auf: Das
   Fenster schrieb nach unten weiter, und was unten hinausläuft, sieht
   niemand. Jetzt rollt es mit, und ein Tipp ohne Klick schreibt sich selbst
   an — man muss den Augenblick nicht mehr treffen.

   **Angeschaltet wird sie unter „Mehr"**, und sie bleibt nur für diesen
   Reiter an (`sessionStorage`). Sie gehört in den ausgelieferten Stand,
   weil der Fehler nur dort auftritt — nicht in Chrome, nicht im Simulator,
   sondern auf dem Telefon in Lucas Hand.
   ══════════════════════════════════════════════════════════════════ */

const SCHLUESSEL = "mm_touchdiagnose";
const hoerer = new Set<() => void>();

function melden(): void {
  hoerer.forEach((h) => h());
}

export function diagnoseAn(): boolean {
  try {
    return sessionStorage.getItem(SCHLUESSEL) === "ja";
  } catch {
    return false;
  }
}

export function diagnoseSetzen(an: boolean): void {
  try {
    if (an) sessionStorage.setItem(SCHLUESSEL, "ja");
    else sessionStorage.removeItem(SCHLUESSEL);
  } catch {
    /* Privater Modus — dann gilt sie für dieses Bild. */
  }
  melden();
}

export function useDiagnose(): boolean {
  return useSyncExternalStore(
    (h) => { hoerer.add(h); return () => hoerer.delete(h); },
    diagnoseAn,
    () => false,
  );
}

/** Ein Element so kurz benennen, dass es in eine Zeile passt. */
function wer(el: Element | null | undefined): string {
  if (!el) return "—";
  const klasse = typeof el.className === "string" && el.className
    ? `.${el.className.split(/\s+/).filter(Boolean).slice(0, 2).join(".")}`
    : "";
  return `${el.tagName}${klasse}`.slice(0, 38);
}

const HOECHSTENS = 26;

/** Wie viele Blätter gerade offen sind. */
function blaetter(): number {
  return document.querySelectorAll(".bvu-schirm, .deck-schirm").length;
}

/** Wie lange ein losgelassener Tipp auf seinen Klick warten darf. */
const FRIST_KLICK = 500;

export function Touchdiagnose() {
  const an = useDiagnose();
  const [zeilen, setzeZeilen] = useState<string[]>([]);
  const liste = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    if (!an) return undefined;
    const beginn = performance.now();
    const wurzel = document.documentElement;
    const schreiben = (text: string) => {
      const ab = Math.round(performance.now() - beginn);
      setzeZeilen((alt) => [...alt.slice(-(HOECHSTENS - 1)), `${String(ab).padStart(5)} ${text}`]);
    };
    const lage = (): string => {
      const sicht = window.visualViewport;
      const tast = wurzel.dataset["tastatur"] ?? "zu";
      const hoehe = getComputedStyle(wurzel).getPropertyValue("--tastatur").trim() || "0px";
      return `tast ${tast} ${hoehe} · sicht ${Math.round(sicht?.width ?? 0)}×${Math.round(sicht?.height ?? 0)}`
        + ` · blatt ${blaetter()} · akt ${wer(document.activeElement)} · fen ${document.hasFocus() ? "ja" : "NEIN"}`;
    };

    /* ── Der Tipp, der nie ankam, schreibt sich selbst an ──
       Ein Klick folgt auf das Loslassen binnen weniger Millisekunden. Kommt
       binnen einer halben Sekunde keiner, ist genau das der Tipp, den Luca
       zweimal machen muss — und die Zeile darüber sagt, was an der Stelle lag. */
    let frist: number | null = null;
    const fristEnde = () => { if (frist !== null) { clearTimeout(frist); frist = null; } };

    /* ── Die Kette einer Berührung (20.09.2026) ──

       Der zweite Mitschrieb vom Gerät zeigte den Fehler, aber nicht seine
       Ursache: `pointerdown` und `pointerup` kamen sauber auf demselben
       Zeichen an, und danach nichts. Der entscheidende Unterschied liegt
       zwischen Zeiger- und Mausereignissen — **kommt `mousedown`?**

       * Kein `mousedown`: Chrome hat die Berührung als Geste verbucht und
         die Mausereignisse unterdrückt. Dann liegt es an Scrollen, Zoom
         oder einem Wurf, der noch läuft (`bausteine/Blattvonunten.tsx`,
         „Die Schwungbremse") — nicht an einem Element.
       * `mousedown`, aber kein `klick`: Die Berührung kam durch, und der
         Klick ging zwischen Nieder- und Losgehen verloren — dann hat sich
         der Baum darunter bewegt.

       Zwei Möglichkeiten, eine Zeile, und der Rest ist Arbeit statt Raten.

       **Seit der Schwungbremse steht auch das `touchmove` in der Kette**
       (20.09.2026), gezählt statt einzeln: `tm14!` heißt vierzehn Stück,
       alle mit `preventDefault()` verbraucht — die Bremse hat gegriffen.
       Ohne `!` war keines abbrechbar; `~` heißt: nur ein Teil. Ein Zug am
       Griff muss `tm…!` tragen, sonst schluckt Android den Tipp danach. */
    let kette: string[] = [];
    let bewegt = 0;
    let gebremst = 0;
    const glied = (code: string) => kette.push(code);
    const bewegung = () => `tm${bewegt}${gebremst === bewegt ? "!" : gebremst ? "~" : ""}`;
    const kettenSatz = () => (kette.length
      ? kette.map((g) => (g === "tm" ? bewegung() : g)).join(" ")
      : "nichts");

    const tipp = (e: PointerEvent) => {
      kette = ["pd"];
      bewegt = 0;
      gebremst = 0;
      const unten = document.elementFromPoint(e.clientX, e.clientY);
      schreiben(`tipp ${Math.round(e.clientX)},${Math.round(e.clientY)} → ${wer(unten)} · ${lage()}`);
    };
    const los = (e: PointerEvent) => {
      glied("pu");
      schreiben(`los  ${Math.round(e.clientX)},${Math.round(e.clientY)} → ${wer(document.elementFromPoint(e.clientX, e.clientY))}`);
      fristEnde();
      frist = window.setTimeout(() => {
        frist = null;
        const grund = kette.includes("md")
          ? "mousedown kam, der Klick ging verloren"
          : "kein mousedown — Chrome verbuchte es als Geste";
        schreiben(`‼ KEIN KLICK · kette ${kettenSatz()} · ${grund}`);
      }, FRIST_KLICK);
    };
    const abbruch = (e: PointerEvent) => {
      fristEnde();
      glied("pc");
      schreiben(`abbruch ${Math.round(e.clientX)},${Math.round(e.clientY)} · kette ${kettenSatz()}`);
    };
    const klick = (e: MouseEvent) => {
      fristEnde();
      glied("kl");
      schreiben(`klick ${wer(e.target as Element)} · kette ${kettenSatz()}`);
    };
    const tastfinger = (e: Event) => {
      if (e.type === "touchmove") {
        bewegt += 1;
        if (e.defaultPrevented) gebremst += 1;
        if (bewegt === 1) glied("tm");
        return;
      }
      glied(`${{ touchstart: "ts", touchend: "te", touchcancel: "tc" }[e.type] ?? e.type}${e.defaultPrevented ? "!" : ""}`);
    };
    const maus = (e: Event) => glied(e.type === "mousedown" ? "md" : "mu");
    const rein = (e: FocusEvent) => schreiben(`fokus rein ${wer(e.target as Element)}`);
    const raus = (e: FocusEvent) => schreiben(`fokus raus ${wer(e.target as Element)} → ${wer(document.activeElement)}`);
    const sicht = () => schreiben(`sicht · ${lage()}`);

    /* Ein Blatt, das auf- oder zugeht, ist der Anfang der Geschichte — und
       `body.overflow` sagt, ob es beim Zugehen auch aufgeräumt hat. */
    let zuletztBlatt = blaetter();
    const beobachter = new MutationObserver(() => {
      const jetzt = blaetter();
      if (jetzt === zuletztBlatt) return;
      schreiben(`blatt ${zuletztBlatt} → ${jetzt} · ovf ${document.body.style.overflow || "—"}`);
      zuletztBlatt = jetzt;
    });
    beobachter.observe(document.body, { childList: true, subtree: true });

    /* Die Kettenglieder hängen am Ende der Bläschenphase: Erst dort steht
       fest, ob jemand unterwegs `preventDefault()` gerufen hat. */
    const tastarten = ["touchstart", "touchmove", "touchend", "touchcancel"] as const;
    document.addEventListener("pointerdown", tipp, true);
    document.addEventListener("pointerup", los, true);
    document.addEventListener("pointercancel", abbruch, true);
    document.addEventListener("click", klick, true);
    document.addEventListener("focusin", rein, true);
    document.addEventListener("focusout", raus, true);
    tastarten.forEach((art) => window.addEventListener(art, tastfinger, { passive: true }));
    window.addEventListener("mousedown", maus, true);
    window.addEventListener("mouseup", maus, true);
    window.visualViewport?.addEventListener("resize", sicht);
    schreiben(`bereit · ${lage()}`);
    return () => {
      fristEnde();
      beobachter.disconnect();
      document.removeEventListener("pointerdown", tipp, true);
      document.removeEventListener("pointerup", los, true);
      document.removeEventListener("pointercancel", abbruch, true);
      document.removeEventListener("click", klick, true);
      document.removeEventListener("focusin", rein, true);
      document.removeEventListener("focusout", raus, true);
      tastarten.forEach((art) => window.removeEventListener(art, tastfinger));
      window.removeEventListener("mousedown", maus, true);
      window.removeEventListener("mouseup", maus, true);
      window.visualViewport?.removeEventListener("resize", sicht);
    };
  }, [an]);

  /* Das Fenster nimmt keine Berührung an (`pointer-events: none`) — also kann
     niemand darin rollen. Es rollt selbst mit, sonst läuft die jüngste und
     damit einzig interessante Zeile unten aus dem Bild. */
  useEffect(() => {
    const el = liste.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [zeilen]);

  if (!an) return null;
  /* Über allem, aber ohne Griff: `pointer-events: none`, damit das Fenster
     selbst keinen Tipp nimmt — es soll messen, nicht mitspielen. */
  return createPortal(
    <div className="tdg" aria-hidden="true">
      <div className="tdg-kopf">
        Touch-Diagnose
        <button
          type="button"
          className="tdg-aus"
          onClick={() => { setzeZeilen([]); diagnoseSetzen(false); }}
        >
          aus
        </button>
      </div>
      <pre className="tdg-liste" ref={liste}>
        {zeilen.map((z, i) => (
          <span key={`${String(i)}-${z}`} className={/‼|abbruch/.test(z) ? "tdg-laut" : undefined}>
            {z}
            {"\n"}
          </span>
        ))}
      </pre>
    </div>,
    document.body,
  );
}
